import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  BackgroundVariant,
} from '@xyflow/react';
import type { Node, Edge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

import type { ActionNode, ActionEdge, FlowStats, FlowEditorProps, ConfiguracionIA, Tarea, ActionNodeData } from '../types';
import { initParse, fetchAccionesTarea, fetchTarea, fetchConfiguracionIA } from '../services/parseService';
import { buildFlowFromAcciones, calculateFlowStats, applyDagreLayout } from '../services/flowBuilder';
import { buildFlowFromDOM, applySimpleLayout } from '../services/domFlowBuilder';

import ActionNodeComponent from './ActionNode';
import NodePanel from './NodePanel';
import Toolbar from './Toolbar';
import StatsPanel from './StatsPanel';

// Custom node types
const nodeTypes = {
  actionNode: ActionNodeComponent,
};

// Inner component with ReactFlow hooks
function FlowEditorInner({
  tareaId,
  embedded = false,
  onClose,
  parseConfig,
  source = 'dom', // Default to DOM for real-time updates
}: FlowEditorProps) {
  const flowRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<ActionNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNode, setSelectedNode] = useState<ActionNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tarea, setTarea] = useState<Tarea | null>(null);
  const [configIA, setConfigIA] = useState<ConfiguracionIA | null>(null);
  const [orientation] = useState<'LR' | 'TB'>('TB');
  const [stats, setStats] = useState<FlowStats | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { fitView, zoomIn, zoomOut, setCenter, getNodes } = useReactFlow();

  // Get tareaId from props or URL params
  const effectiveTareaId = useMemo(() => {
    if (tareaId) return tareaId;

    // Try URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlTareaId = urlParams.get('tareaId');
    if (urlTareaId) return urlTareaId;

    // Try parent window
    const parentWindow = window.parent || window.opener;
    if (parentWindow) {
      try {
        const parentTareaId = (parentWindow as any).$?.('#TareaId')?.val?.();
        if (parentTareaId) return parentTareaId;

        // Try direct access
        const tareaActual = (parentWindow as any).tareaActual;
        if (tareaActual?.id) return tareaActual.id;
      } catch (e) {
        // Cross-origin access denied
      }
    }

    return null;
  }, [tareaId]);

  // Helper to update nodes with orientation
  const updateNodesWithOrientation = useCallback((
    flowNodes: ActionNode[],
    flowEdges: ActionEdge[],
    newOrientation: 'LR' | 'TB'
  ) => {
    // Add orientation to each node's data
    const nodesWithOrientation = flowNodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        orientation: newOrientation,
      },
    }));

    // Apply layout
    const positionedNodes = applyDagreLayout(nodesWithOrientation, flowEdges, newOrientation);
    return positionedNodes;
  }, []);

  // Load data
  useEffect(() => {
    async function loadData() {
      try {
        setIsLoading(true);
        setError(null);

        let flowNodes: ActionNode[];
        let flowEdges: ActionEdge[];

        if (source === 'dom') {
          // Read from DOM (real-time, no server fetch)
          const result = buildFlowFromDOM();
          flowNodes = result.nodes;
          flowEdges = result.edges;

          if (flowNodes.length === 0) {
            setError('No hay acciones en el formulario');
            setIsLoading(false);
            return;
          }

          // Apply simple layout for DOM mode
          const positionedNodes = applySimpleLayout(flowNodes, flowEdges, orientation);
          setNodes(positionedNodes as Node<ActionNodeData>[]);
          setEdges(flowEdges as Edge[]);
          setStats(calculateFlowStats(positionedNodes, flowEdges));

        } else {
          // Fetch from Parse Server
          if (!effectiveTareaId) {
            setError('No se especificó ID de tarea');
            setIsLoading(false);
            return;
          }

          initParse(parseConfig);

          const [acciones, tareaData, iaConfig] = await Promise.all([
            fetchAccionesTarea(effectiveTareaId),
            fetchTarea(effectiveTareaId),
            fetchConfiguracionIA(effectiveTareaId),
          ]);

          if (acciones.length === 0) {
            setError('No hay acciones en este formulario');
            setIsLoading(false);
            return;
          }

          setTarea(tareaData);
          setConfigIA(iaConfig);

          const result = buildFlowFromAcciones(acciones, iaConfig);
          flowNodes = result.nodes;
          flowEdges = result.edges;

          const positionedNodes = updateNodesWithOrientation(flowNodes, flowEdges, orientation);
          setNodes(positionedNodes as Node<ActionNodeData>[]);
          setEdges(flowEdges as Edge[]);
          setStats(calculateFlowStats(positionedNodes, flowEdges));
        }

        setIsLoading(false);

        // Fit view after render
        setTimeout(() => {
          fitView({ padding: 0.2, duration: 500 });
        }, 100);

      } catch (err) {
        console.error('Error loading flow data:', err);
        setError(err instanceof Error ? err.message : 'Error cargando datos');
        setIsLoading(false);
      }
    }

    loadData();
  }, [effectiveTareaId, parseConfig, setNodes, setEdges, fitView, orientation, updateNodesWithOrientation, source]);

  // Handle node click - disabled, no detail panel
  // const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
  //   setSelectedNode(node as ActionNode);
  // }, []);

  // Handle background click
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);


  // Export functionality
  const handleExport = useCallback(async (format: 'png' | 'png-transparent' | 'pdf') => {
    const flowElement = document.querySelector('.react-flow__viewport') as HTMLElement;
    if (!flowElement) return;

    setIsExporting(true);

    try {
      // Get the container bounds
      const nodesBounds = document.querySelector('.react-flow__nodes') as HTMLElement;
      if (!nodesBounds) return;

      const options = {
        backgroundColor: format === 'png-transparent' ? 'transparent' : '#ffffff',
        quality: 1,
        pixelRatio: 2,
        // Skip external fonts and stylesheets to avoid CORS errors
        skipFonts: true,
        filter: (node: Element) => {
          // Skip external stylesheets and fonts
          if (node instanceof HTMLLinkElement) {
            const href = node.getAttribute('href') || '';
            if (href.includes('fonts.googleapis') ||
                href.includes('font-awesome') ||
                href.includes('cdn.jsdelivr')) {
              return false;
            }
          }
          return true;
        },
      };

      const dataUrl = await toPng(flowElement, options);

      if (format === 'pdf') {
        // Create PDF
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve) => { img.onload = resolve; });

        const pdf = new jsPDF({
          orientation: img.width > img.height ? 'landscape' : 'portrait',
          unit: 'px',
          format: [img.width / 2, img.height / 2],
        });

        pdf.addImage(dataUrl, 'PNG', 0, 0, img.width / 2, img.height / 2);
        pdf.save(`flujo-${tarea?.Nombre || 'formulario'}.pdf`);
      } else {
        // Download PNG
        const link = document.createElement('a');
        link.download = `flujo-${tarea?.Nombre || 'formulario'}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
      console.error('Error exporting:', err);
      alert('Error al exportar. Intente de nuevo.');
    } finally {
      setIsExporting(false);
    }
  }, [tarea]);

  // Reset view to start
  const handleResetView = useCallback(() => {
    const allNodes = getNodes();
    // Find first node (orden=1 or just first in array)
    const firstNode = allNodes.find(n => n.data?.orden === 1) || allNodes[0];
    if (firstNode) {
      setCenter(firstNode.position.x + 100, firstNode.position.y + 30, {
        zoom: 0.8,
        duration: 500,
      });
    }
  }, [getNodes, setCenter]);

  // Loading state
  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f9fafb',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: '3px solid #e5e7eb',
              borderTopColor: '#6b7280',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 12px',
            }}
          />
          <p style={{ color: '#6b7280', fontSize: 13 }}>Cargando flujo...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f9fafb',
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h3 style={{ color: '#1f2937', fontWeight: 600, marginBottom: 8, fontSize: 15 }}>Error</h3>
          <p style={{ color: '#6b7280', fontSize: 13 }}>{error}</p>
          {embedded && onClose && (
            <button
              onClick={onClose}
              style={{
                marginTop: 16,
                padding: '8px 16px',
                background: '#1f2937',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div ref={flowRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Close button - Miro style, top right */}
      {embedded && onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 50,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            transition: 'background 0.15s ease, border-color 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f3f4f6';
            e.currentTarget.style.borderColor = '#d1d5db';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.borderColor = '#e5e7eb';
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}

      {/* Export loading overlay */}
      {isExporting && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255,255,255,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 32,
                height: 32,
                border: '3px solid #e5e7eb',
                borderTopColor: '#6b7280',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 12px',
              }}
            />
            <p style={{ color: '#6b7280', fontSize: 13 }}>Exportando...</p>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <Toolbar
        onExport={handleExport}
        onFitView={() => fitView({ padding: 0.2, duration: 500 })}
        onResetView={handleResetView}
        onZoomIn={() => zoomIn({ duration: 300 })}
        onZoomOut={() => zoomOut({ duration: 300 })}
        onToggleOrientation={() => {}}
        orientation={orientation}
      />

      {/* Stats panel */}
      {stats && (
        <StatsPanel stats={stats} tareaName={tarea?.Nombre} />
      )}

      {/* Node details panel */}
      <NodePanel
        node={selectedNode}
        configIA={configIA}
        onClose={() => setSelectedNode(null)}
      />

      {/* React Flow */}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
                onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false,
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={24}
          size={1}
          color="#d1d5db"
        />

        <MiniMap
          nodeStrokeWidth={2}
          zoomable
          pannable
          position="bottom-right"
          style={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
          nodeColor={(node) => {
            const data = node.data as ActionNodeData;
            if (data?.tieneIA) return '#10b981';
            if (data?.tipo === 'grupo') return '#f59e0b';
            if (data?.tipo === 'inicio') return '#3b82f6';
            if (data?.tipo === 'fin') return '#ef4444';
            return '#94a3b8';
          }}
        />
      </ReactFlow>
    </div>
  );
}

// Wrapper with provider
export default function FlowEditor(props: FlowEditorProps) {
  return (
    <ReactFlowProvider>
      <FlowEditorInner {...props} />
    </ReactFlowProvider>
  );
}
